/**
 * mr-sticky-atc.js — Sprint 6 Phase 3 (comments updated 2026-07-11 after
 * the Phase 4/5-gate real-device QA rebuild of snippets/mr-c-sticky-atc.liquid)
 *
 * Purpose: show mr-c-sticky-atc (the mobile-only sticky Add-to-Cart bar)
 * once the real, in-page buy button has scrolled COMPLETELY out of view,
 * and hide it the instant the real button is visible again or the
 * customer is focused on typing into any field — so the bar never
 * duplicates, covers, or fights the real buy area or the on-screen
 * keyboard.
 *
 * MOBILE-ONLY IS CSS's JOB, NOT THIS SCRIPT'S — see mr-t-product.css.
 *
 * NO SCROLL-EVENT LISTENERS: visibility is driven entirely by
 * IntersectionObserver watching the real buy button, threshold:0 — the
 * callback's `isIntersecting` is true the instant ANY part of the anchor
 * is on-screen and false only once it is 100% off-screen, which is
 * exactly "original buy buttons completely outside viewport → show;
 * visible again → immediately hide."
 *
 * ANCHOR SELECTION (real-device QA fix, 2026-07-11): the real buy_buttons
 * block in sections/mr-s-product-commitment.liquid now carries an
 * explicit `[data-mr-buy-anchor]` attribute, so this script always finds
 * the correct, singular anchor deterministically — no dependency on
 * DOM-order-based matching. The `.product-form__submit` fallback below is
 * now pure defense-in-depth (kept for any other page/harness that hasn't
 * opted in explicitly); on the real product-commitment section it should
 * never actually be needed, since the sticky bar itself no longer
 * contains any `.product-form__submit`-classed element to accidentally
 * self-match — its own button uses a distinct class
 * (`.mr-c-sticky-atc__submit`) and submits the real form via the native
 * HTML `form="..."` attribute rather than rendering a second form.
 *
 * NEVER COVERS BENEFICIARY FIELDS / KEYBOARD: any `input`, `textarea`,
 * or `select` receiving focus anywhere on the page immediately hides the
 * bar (`focusin`), and it's eligible to reappear on `focusout` only if
 * the buy button is still off-screen.
 *
 * No mutation of Infinite Options state, no PII anywhere (this script
 * never reads field values, only focus/blur events and element
 * geometry), zero console errors on load or interaction.
 *
 * Progressive enhancement: if this script fails to load, the bar simply
 * never reveals (CSS default is `display:none` unconditionally) — the
 * always-visible in-page (non-sticky) buy button is completely
 * unaffected either way.
 */
(function () {
  'use strict';

  var STICKY_SELECTOR = '.mr-c-sticky-atc';
  var VISIBLE_CLASS = 'mr-c-sticky-atc--visible';
  var EXPLICIT_ANCHOR_SELECTOR = '[data-mr-buy-anchor]';
  var FALLBACK_ANCHOR_SELECTOR = '.product-form__submit';
  var FOCUSABLE_SELECTOR = 'input, textarea, select';

  function findAnchor(sticky) {
    var explicit = document.querySelector(EXPLICIT_ANCHOR_SELECTOR);
    if (explicit && !sticky.contains(explicit)) return explicit;

    var candidates = document.querySelectorAll(FALLBACK_ANCHOR_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
      if (!sticky.contains(candidates[i])) return candidates[i];
    }
    return null; // no usable anchor — bar stays inert (never reveals)
  }

  function initSticky(sticky) {
    var anchor = findAnchor(sticky);
    if (!anchor) return;

    var anchorVisible = true; // assume visible until the observer's first callback, avoiding a flash-of-bar before layout settles
    var fieldFocused = false;

    function sync() {
      var shouldShow = !anchorVisible && !fieldFocused;
      sticky.classList.toggle(VISIBLE_CLASS, shouldShow);
    }

    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[entries.length - 1];
        anchorVisible = entry.isIntersecting;
        sync();
      },
      { root: null, threshold: 0 }
    );
    observer.observe(anchor);

    function onFocusIn(event) {
      if (event.target && event.target.matches && event.target.matches(FOCUSABLE_SELECTOR)) {
        fieldFocused = true;
        sync();
      }
    }
    function onFocusOut(event) {
      if (event.target && event.target.matches && event.target.matches(FOCUSABLE_SELECTOR)) {
        fieldFocused = false;
        sync();
      }
    }

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
  }

  function init(root) {
    var scope = root || document;
    var stickies = scope.querySelectorAll(STICKY_SELECTOR);
    stickies.forEach(function (sticky) {
      initSticky(sticky);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Exposed for Phase 4 re-init and isolated QA-harness testing.
  window.MRStickyAtc = { init: init };
})();
